/**
 * Per-route meta-tag builder for the public-website SPA (task 10.1,
 * design §6.2 / Requirement 11.6 / 11.7 / 11.8).
 *
 * `buildMeta` is a **pure** function: given a route, optional
 * per-record overrides (sourced from the API response on record
 * routes), and the brand-level `BrandingConfig.share.*` fallbacks,
 * it returns the data structure that the `<SeoHead>` boundary
 * (task 10.2) renders via `react-helmet-async`. No React, no I/O,
 * no module-level state.
 *
 * **Graceful omission of TODO-027 / TODO-028 / TODO-029** is the
 * core invariant per the Requirement 11.6 / 11.8 footnote: when a
 * `share.defaultImage` / `share.twitterHandle` / `share.tagline` /
 * `share.description` value is undefined, the corresponding
 * meta-tag entry is **dropped from the output array** rather than
 * emitted with an empty `content=""`. The renderer (`<SeoHead>`)
 * therefore never has to decide what to suppress — the builder
 * has already done it.
 *
 * The shape follows the design §6.2 table:
 *
 * | Route | `<title>` | description | OG | JSON-LD |
 * |---|---|---|---|---|
 * | `/`            | `share.tagline`        | `share.description` | site-default | `WebSite`   |
 * | `/daily/{d}`   | `DaySummary.title`     | `hookPhrase`        | per-day      | `Article`   |
 * | `/games/{id}`  | `GameDetail.title`     | summary truncated   | per-game     | `VideoGame` |
 * | other          | route-name + DVGUP     | `share.description` | site-default | `WebSite`   |
 *
 * The non-record routes inherit `share.*`. Record routes pass the
 * per-record values via `pageTitle` / `pageDescription` /
 * `pageImage` / `jsonLdType` / `jsonLdData`.
 *
 * Validates: Requirement 11.6, 11.7, 11.8 (consumer side).
 */

import type { ShareConfig } from '@dvgup/contracts/branding-config';

/**
 * Default site origin used when `MetaInput.siteOrigin` is omitted.
 * Bound to the public domain per TODO-007 (ANSWERED — `dvgup.com`).
 */
const DEFAULT_SITE_ORIGIN = 'https://dvgup.com';

/**
 * Default site name used when `MetaInput.siteName` is omitted and
 * the share tagline / page title also lacks a brand suffix.
 */
const DEFAULT_SITE_NAME = 'DVGUP';

/** Brand-suffix appended to titles that don't already mention `DVGUP`. */
const TITLE_SUFFIX = ' | DVGUP';

/** OpenGraph `og:type` value per JSON-LD `@type`. */
const OG_TYPE_BY_JSONLD_TYPE: Readonly<Record<JsonLdType, string>> = {
  WebSite: 'website',
  Article: 'article',
  VideoGame: 'product',
};

/**
 * The three JSON-LD `@type` values the public-website emits per
 * Requirement 11.8.
 */
export type JsonLdType = 'WebSite' | 'Article' | 'VideoGame';

/**
 * One OpenGraph meta-tag entry. Rendered by `<SeoHead>` as
 * `<meta property={property} content={content} />`.
 */
export interface OpenGraphTag {
  readonly property: string;
  readonly content: string;
}

/**
 * One Twitter-card meta-tag entry. Rendered by `<SeoHead>` as
 * `<meta name={name} content={content} />`.
 */
export interface TwitterCardTag {
  readonly name: string;
  readonly content: string;
}

/**
 * The full data structure consumed by `<SeoHead>` (task 10.2). Every
 * field except `jsonLd` is required — the route always carries a
 * `<title>`, a `<meta name="description">`, and a canonical URL per
 * Requirement 11.7. JSON-LD is optional because the catch-all 404
 * route does not emit structured data.
 */
export interface MetaTags {
  readonly title: string;
  readonly description: string;
  readonly canonical: string;
  readonly openGraph: readonly OpenGraphTag[];
  readonly twitterCard: readonly TwitterCardTag[];
  readonly jsonLd?: Record<string, unknown>;
}

/**
 * Builder input. Per-route containers (`DailyPage`, `GamePage`, …)
 * supply their own values from the API response; non-record routes
 * pass only `branding`.
 */
export interface MetaInput {
  /** Route path beginning with `/`, e.g. `/`, `/daily/2024-05-12`, `/games/halo-infinite`. */
  readonly route: string;
  /** Brand site name, e.g. `DVGUP`. Defaults to `DVGUP`. */
  readonly siteName?: string;
  /** Origin used to build the canonical URL. Defaults to `https://dvgup.com`. */
  readonly siteOrigin?: string;
  /** Per-record title override; takes precedence over `branding.tagline`. */
  readonly pageTitle?: string;
  /** Per-record description override; takes precedence over `branding.description`. */
  readonly pageDescription?: string;
  /** Absolute URL of the per-record share image (e.g. `GameDetail.primaryImage`). */
  readonly pageImage?: string;
  /** `BrandingConfig.share` slot — every field optional per TODO-027 / TODO-028 / TODO-029. */
  readonly branding: ShareConfig | undefined;
  /** JSON-LD `@type` to emit. Omit to emit no JSON-LD. */
  readonly jsonLdType?: JsonLdType;
  /** Per-record fields merged into the JSON-LD object (e.g. `name`, `image`, `datePublished`). */
  readonly jsonLdData?: Record<string, unknown>;
}

/**
 * Build the per-route meta-tag bundle.
 *
 * Resolution rules:
 * - `title` = `pageTitle ?? branding.tagline ?? siteName ?? 'DVGUP'`,
 *   with `' | DVGUP'` appended when the resolved value does not
 *   already contain `DVGUP` (case-insensitive).
 * - `description` = `pageDescription ?? branding.description ?? siteName ?? 'DVGUP'`.
 * - `canonical` = `${siteOrigin ?? 'https://dvgup.com'}${route}`.
 * - OpenGraph emits `og:title`, `og:type`, `og:url`, `og:description`,
 *   `og:site_name`. `og:image` is emitted ONLY when `pageImage` or
 *   `branding.defaultImage` is defined (TODO-027 graceful omit).
 * - Twitter card emits `twitter:card` (`summary_large_image` if image
 *   present, `summary` otherwise), `twitter:title`, `twitter:description`.
 *   `twitter:image` is emitted only when an image is resolved;
 *   `twitter:site` is emitted only when `branding.twitterHandle`
 *   is defined (TODO-028 graceful omit).
 * - JSON-LD emits a base shape per `jsonLdType` and merges
 *   `jsonLdData` shallowly on top. The `@context` and `@type`
 *   fields are pinned by the builder and override any conflicting
 *   keys in `jsonLdData`.
 *
 * @param input Per-route meta-tag inputs.
 * @returns The per-route meta-tag bundle for `<SeoHead>` to render.
 */
export function buildMeta(input: MetaInput): MetaTags {
  const siteName = input.siteName ?? DEFAULT_SITE_NAME;
  const siteOrigin = input.siteOrigin ?? DEFAULT_SITE_ORIGIN;
  const branding = input.branding;

  const rawTitle = input.pageTitle ?? branding?.tagline ?? siteName;
  const title = appendBrandSuffix(rawTitle, siteName);

  const description = input.pageDescription ?? branding?.description ?? siteName;

  const canonical = `${siteOrigin}${input.route}`;

  const image = input.pageImage ?? branding?.defaultImage;
  const ogType = OG_TYPE_BY_JSONLD_TYPE[input.jsonLdType ?? 'WebSite'];

  const openGraph: OpenGraphTag[] = [
    { property: 'og:title', content: title },
    { property: 'og:type', content: ogType },
    { property: 'og:url', content: canonical },
    { property: 'og:description', content: description },
    { property: 'og:site_name', content: siteName },
  ];
  if (image !== undefined) {
    openGraph.push({ property: 'og:image', content: image });
  }

  const twitterCard: TwitterCardTag[] = [
    { name: 'twitter:card', content: image !== undefined ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];
  if (image !== undefined) {
    twitterCard.push({ name: 'twitter:image', content: image });
  }
  if (branding?.twitterHandle !== undefined) {
    twitterCard.push({ name: 'twitter:site', content: branding.twitterHandle });
  }

  const jsonLd =
    input.jsonLdType !== undefined ? buildJsonLd(input.jsonLdType, input.jsonLdData) : undefined;

  return {
    title,
    description,
    canonical,
    openGraph,
    twitterCard,
    ...(jsonLd !== undefined ? { jsonLd } : {}),
  };
}

/**
 * Append `' | DVGUP'` to titles that don't already mention the
 * brand. The check is case-insensitive against the `siteName`, so
 * a tagline like `'DVGUP — daily updates'` does not pick up a
 * second `DVGUP` suffix.
 */
function appendBrandSuffix(title: string, siteName: string): string {
  const needle = siteName.toLowerCase();
  if (title.toLowerCase().includes(needle)) {
    return title;
  }
  return `${title}${TITLE_SUFFIX}`;
}

/**
 * Build the JSON-LD object for the requested type. The base shape
 * pins `@context` and `@type`; `data` overrides everything else.
 */
function buildJsonLd(
  type: JsonLdType,
  data: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return {
    ...(data ?? {}),
    '@context': 'https://schema.org',
    '@type': type,
  };
}
